import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { BasicInfoSection } from "@/components/forms/BasicInfoSection";
import { ContactInfoSection } from "@/components/forms/ContactInfoSection";
import { AddressSection } from "@/components/forms/AddressSection";
import { ImageUploadSection } from "@/components/forms/ImageUploadSection";
import { businessProfileSchema, type BusinessProfileFormData } from "@/types/business-profile";

interface BusinessProfileFormProps {
  initialData?: Partial<BusinessProfileFormData>;
  currentImage?: string | null;
  onSubmit: (data: BusinessProfileFormData) => Promise<void>;
  onImageUpload?: (file: File) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  imageLoading?: boolean;
  className?: string;
}

export const BusinessProfileForm: React.FC<BusinessProfileFormProps> = ({
  initialData,
  currentImage,
  onSubmit,
  onImageUpload,
  onCancel,
  loading = false,
  imageLoading = false,
  className,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BusinessProfileFormData>({
    resolver: zodResolver(businessProfileSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      website: initialData?.website || "",
      industry: initialData?.industry || "",
      designation: initialData?.designation || "",
      company_size: initialData?.company_size || "",
      contact_info: {
        email: initialData?.contact_info?.email || "",
        phone: initialData?.contact_info?.phone || "",
        address: {
          line1: initialData?.contact_info?.address?.line1 || "",
          line2: initialData?.contact_info?.address?.line2 || "",
          city: initialData?.contact_info?.address?.city || "",
          state: initialData?.contact_info?.address?.state || "",
          postal_code: initialData?.contact_info?.address?.postal_code || "",
          country: initialData?.contact_info?.address?.country || "",
        },
      },
    },
  });

  const handleFormSubmit = async (data: BusinessProfileFormData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <Card
      className={cn(
        "w-full max-w-4xl mx-auto",
        "shadow-lg shadow-black/5",
        "relative",
        className
      )}
    >
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-foreground">
          Business Profile
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Update your business information and contact details
        </CardDescription>
      </CardHeader>
      <CardContent className="relative z-10">
        <div className="space-y-6">
          {/* Image Upload Section */}
          <ImageUploadSection
            currentImage={currentImage}
            onImageUpload={onImageUpload}
            imageLoading={imageLoading}
          />

          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            {/* Basic Information */}
            <BasicInfoSection
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              isSubmitting={isSubmitting}
              loading={loading}
            />

            {/* Contact Information */}
            <ContactInfoSection
              register={register}
              errors={errors}
              isSubmitting={isSubmitting}
              loading={loading}
            />

            {/* Address Information */}
            <AddressSection
              register={register}
              errors={errors}
              isSubmitting={isSubmitting}
              loading={loading}
            />

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isSubmitting || loading}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                loading={isSubmitting || loading}
                disabled={isSubmitting || loading}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};
